import { i18n } from '@/common/locale';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isNaN } from 'lodash-es';
import { useMyInfoStore } from '@/config/stores/my-info';
import { TimeRangeInfo, TimezoneName } from '@/common/utils';
import type { ReadOnlyTimes } from '@/types/common.types';
import type { TimePeriodInfo, TimeUnit } from './types';
import {
  UTC_TIME_FORMAT,
  GLOBAL_TIME_RANGE_FORMAT,
  GLOBAL_TIME_LIVE_FORMAT,
  CUSTOM_PERIOD_PREFIX,
  TIME_UNITS,
} from './constants';
import { returnMatchingFormat, parsePeriod } from '@/common/utils/core/time/parse';
import { stringifyPeriod } from '@/common/utils/core/time/stringify';
import { getSafeDateTime, getDiffSecond } from '@/common/utils/core/time/calculate';
import { toTimezoneKeepingWallClock } from '@/common/utils/browser/conversion';

dayjs.extend(customParseFormat);

/**
 * 초를 사람이 읽기 쉬운 문자열로 변환 (예: 3661 -> '1 Hour 1 Minute 1 Second')
 * @param totalSeconds 총 초
 * @returns 사람이 읽기 쉬운 문자열
 */
export const stringifyDateString = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return '';

  let remainingSeconds: number = totalSeconds;
  const result: string[] = [];

  (TIME_UNITS as readonly TimeUnit[]).forEach((unit) => {
    const count = Math.floor(remainingSeconds / unit.duration);
    remainingSeconds %= unit.duration;

    if (count > 0) {
      const translationKey = count > 1 ? unit.labelPlural : unit.labelSingle;
      const text = i18n.global.t(translationKey, { value: count });
      result.push(text);
    }
  });

  return result.join(' ');
};

/**
 * 기간 값을 로케일 라벨로 변환 (예: 'p5m' -> 'Past 5 Minutes')
 * @param value 기간 값
 * @returns 로케일 라벨
 */
export const getLocaleLabel = (value: string): string => {
  const units = {
    s: 'SECOND',
    m: 'MINUTE',
    h: 'HOUR',
    d: 'DAY',
  };

  const match = value.match(/^p(\d+)([mhds])$/);
  if (match) {
    const [, num, unit] = match;
    const unitLocaleKey = `WORD.${units[unit]}${+num > 1 ? 'S' : ''}`;
    return `${i18n.global.t('WORD.PAST')} ${i18n.global.t(unitLocaleKey, { value: num })}`;
  }

  return value;
};

// ============================================================
// Time Calculation - 시간 계산 유틸리티
// ============================================================

/**
 * instant 를 대상 timezone 의 벽시계 문자열로 변환
 *
 * `toTimezoneKeepingWallClock` 결과에 `.format()` 을 직접 쓰면 안 되는 이유는 그쪽 JSDoc 에 있다.
 */
const formatInTimezone = (instant: Dayjs, timezone: TimezoneName, format: string): string =>
  dayjs.utc(instant.valueOf()).tz(timezone).format(format);

/**
 * 시간에 오프셋 적용
 */
export function adjustTimeByOffset({
  time,
  offset,
  operation,
  timezone,
  format = GLOBAL_TIME_RANGE_FORMAT,
}: {
  time: string;
  offset: number;
  operation: 'add' | 'subtract';
  timezone?: TimezoneName;
  format?: string;
}): string {
  const { timezone: tz } = useMyInfoStore();
  const targetTimezone = timezone || tz;
  const baseDayjs = toTimezoneKeepingWallClock(
    dayjs(time, returnMatchingFormat(time)),
    targetTimezone,
  );

  const adjusted =
    operation === 'add' ? baseDayjs.add(offset, 's') : baseDayjs.subtract(Math.abs(offset), 's');
  return formatInTimezone(adjusted, targetTimezone, format);
}

/**
 * 현재 시간이 초과되었는지 확인
 */
export const isExceededCurrentTime = ({ time }: { time: string }): boolean => {
  const { timezone: tz } = useMyInfoStore();
  return (
    toTimezoneKeepingWallClock(dayjs(time, returnMatchingFormat(time)), tz).unix() >
    dayjs.tz().unix()
  );
};

// ============================================================
// Time Range - 시간 범위 유틸리티
// ============================================================

/**
 * 앞으로 이동한 시간 범위 계산
 */
export const getForwardTimeRange = ({
  fromTime,
  toTime,
  maxTimeRangeSecLimit,
  timezone,
}: {
  fromTime: string;
  toTime: string;
  maxTimeRangeSecLimit?: number;
  timezone?: TimezoneName;
}): { fromTime: string; toTime: string } => {
  let offset = getDiffSecond(fromTime, toTime);
  let refinedToTime = toTime;

  if (maxTimeRangeSecLimit && offset > maxTimeRangeSecLimit) {
    offset = maxTimeRangeSecLimit;
    refinedToTime = adjustTimeByOffset({
      time: fromTime,
      operation: 'add',
      offset,
      timezone,
    });
  }

  return {
    fromTime: adjustTimeByOffset({
      time: fromTime,
      operation: 'add',
      offset,
      timezone,
    }),
    toTime: adjustTimeByOffset({
      time: refinedToTime,
      operation: 'add',
      offset,
      timezone,
    }),
  };
};

/**
 * 뒤로 이동한 시간 범위 계산
 */
export const getBackTimeRange = ({
  fromTime,
  toTime,
  maxTimeRangeSecLimit,
  timezone,
}: {
  fromTime: string;
  toTime: string;
  maxTimeRangeSecLimit?: number;
  timezone?: TimezoneName;
}): { fromTime: string; toTime: string } => {
  let offset = getDiffSecond(fromTime, toTime);
  let refinedFromTime = fromTime;

  if (maxTimeRangeSecLimit && offset > maxTimeRangeSecLimit) {
    offset = maxTimeRangeSecLimit;
    refinedFromTime = adjustTimeByOffset({
      time: toTime,
      operation: 'subtract',
      offset,
      timezone,
    });
  }

  return {
    fromTime: adjustTimeByOffset({
      time: refinedFromTime,
      operation: 'subtract',
      offset,
      timezone,
    }),
    toTime: adjustTimeByOffset({
      time: toTime,
      operation: 'subtract',
      offset,
      timezone,
    }),
  };
};

/**
 * 현재 시간 기준 시간 범위 계산
 */
export const getCurrentTimeRange = ({
  period,
  timeFormat = GLOBAL_TIME_RANGE_FORMAT,
  fixedTimes,
  direction = 'past',
  timezone,
}: {
  period: string;
  timeFormat?: string;
  fixedTimes?: {
    hour?: number | undefined;
    minute?: number | undefined;
    second?: number | undefined;
  };
  direction?: 'past' | 'future';
  timezone?: TimezoneName;
}): { fromTime: string; toTime: string } => {
  let currentTime = timezone ? dayjs.tz(undefined, timezone) : dayjs.tz();

  if (period === 'today') {
    return {
      fromTime: currentTime.startOf('day').format(timeFormat),
      toTime: currentTime.add(1, 'day').startOf('day').format(timeFormat),
    };
  }

  const { offset, unit } = parsePeriod(period);

  if (fixedTimes?.second !== undefined && fixedTimes.second > -1) {
    currentTime = currentTime.second(fixedTimes.second);
  }

  if (fixedTimes?.minute !== undefined && fixedTimes.minute > -1) {
    currentTime = currentTime.minute(fixedTimes.minute);
  }

  if (fixedTimes?.hour !== undefined && fixedTimes.hour > -1) {
    currentTime = currentTime.hour(fixedTimes.hour);
  }

  if (direction === 'future') {
    return {
      fromTime: currentTime.format(timeFormat),
      toTime: getSafeDateTime(currentTime.add(offset, unit)).format(timeFormat),
    };
  }

  // 일 단위 조회(h/m/s 모두 0 고정): toTime에 1일 추가하여 당일 데이터 포함 (지난 ~일 클릭시 오늘 날짜가 조회 안된다는 이슈)
  // fromTime도 adjustedToTime 기준으로 계산하여 조회 범위가 정확히 N일이 되도록 유지
  const isDayLevelFixed =
    fixedTimes?.hour === 0 && fixedTimes?.minute === 0 && fixedTimes?.second === 0;

  const adjustedToTime = isDayLevelFixed ? currentTime.add(1, 'day') : currentTime;
  return {
    fromTime: getSafeDateTime(adjustedToTime.subtract(offset, unit)).format(timeFormat),
    toTime: adjustedToTime.format(timeFormat),
  };
};

export const getFixedTimesByReadonlyTimes = (
  readonlyTimes?: ReadOnlyTimes,
): { hour?: number; minute?: number; second?: number } | undefined => {
  if (!readonlyTimes) {
    return undefined;
  }

  const fixedTimes: { hour?: number; minute?: number; second?: number } = {};

  if (readonlyTimes.hour) {
    fixedTimes.hour = 0;
  }
  if (readonlyTimes.minute) {
    fixedTimes.minute = 0;
  }
  if (readonlyTimes.second) {
    fixedTimes.second = 0;
  }

  return Object.keys(fixedTimes).length ? fixedTimes : undefined;
};

/**
 * 시간 범위 포맷팅
 */
export const formatTimeRange = ({
  fromTime,
  toTime,
  timeFormat = GLOBAL_TIME_RANGE_FORMAT,
}: {
  fromTime: string;
  toTime: string;
  timeFormat?: string;
}): { fromTime: string; toTime: string } => {
  return {
    fromTime: dayjs(fromTime).format(timeFormat),
    toTime: dayjs(toTime).format(timeFormat),
  };
};

/**
 * 시간 범위를 UTC로 변환
 */
export const formatTimeRangeToUtc = ({
  fromTime,
  toTime,
  timezone,
}: TimeRangeInfo & { timezone?: TimezoneName }): { fromTimeUtc: string; toTimeUtc: string } => {
  const { timezone: userTz } = useMyInfoStore();
  const tz = timezone ?? userTz;
  return {
    fromTimeUtc: toTimezoneKeepingWallClock(dayjs(fromTime, returnMatchingFormat(fromTime)), tz)
      .utc()
      .format(UTC_TIME_FORMAT),
    toTimeUtc: toTimezoneKeepingWallClock(dayjs(toTime, returnMatchingFormat(toTime)), tz)
      .utc()
      .format(UTC_TIME_FORMAT),
  };
};

/**
 * UTC 시간 범위를 타임존으로 변환
 */
export const formatTimeRangeToTz = ({
  fromTimeUtc,
  toTimeUtc,
  tz,
}: {
  fromTimeUtc: string;
  toTimeUtc: string;
  tz?: TimezoneName;
}): { fromTime: string; toTime: string } => {
  const resolvedTz = tz ?? useMyInfoStore().timezone;
  return {
    fromTime: dayjs(fromTimeUtc, returnMatchingFormat(fromTimeUtc))
      .utc(true)
      .tz(resolvedTz)
      .format(GLOBAL_TIME_RANGE_FORMAT),
    toTime: dayjs(toTimeUtc, returnMatchingFormat(toTimeUtc))
      .utc(true)
      .tz(resolvedTz)
      .format(GLOBAL_TIME_RANGE_FORMAT),
  };
};

// ============================================================
// Period Utilities - 기간 유틸리티
// ============================================================

/**
 * 기간 문자열에서 custom 접두사 제거 및 최대 범위 제한 적용
 */
export const getFormalPeriod = (period: string, maxTimeRangeSecLimit?: number): string => {
  const periodValue = period.includes(CUSTOM_PERIOD_PREFIX)
    ? period.slice(CUSTOM_PERIOD_PREFIX.length)
    : period;

  if (maxTimeRangeSecLimit && !isNaN(maxTimeRangeSecLimit)) {
    let refinedPeriod = periodValue;
    const { offset } = parsePeriod(periodValue);
    if (offset > maxTimeRangeSecLimit) {
      refinedPeriod = stringifyPeriod({ totalSeconds: maxTimeRangeSecLimit });
    }

    return refinedPeriod;
  }

  return periodValue;
};

// ============================================================
// TimePeriodInfo Utilities - TimePeriodInfo 유틸리티
// ============================================================

/**
 * 주어진 기준 시간과 period로 TimePeriodInfo 생성
 * @param currentTime 기준 시간 문자열
 * @param timePeriod 기간 문자열 (예: 'p5m', 'p1h', 'current')
 * @param isPaused 일시정지 여부
 * @param timeFormat 시간 포맷
 */
export const createTimePeriodInfoFromCurrentTime = ({
  currentTime,
  timePeriod,
  isPaused = false,
  timeFormat = GLOBAL_TIME_RANGE_FORMAT,
}: {
  currentTime: string;
  timePeriod: string;
  isPaused?: boolean;
  timeFormat?: string;
}): TimePeriodInfo => {
  // PERIOD 모드: fromTime = currentTime - offset, toTime = currentTime
  const { offset, unit } = parsePeriod(timePeriod);
  const { timezone: tz } = useMyInfoStore();

  // currentTime 은 계정 tz 벽시계 문자열(`getServerCurrentTime()` 결과)이므로 기간 뺄셈도
  // 계정 tz 프레임에서 해야 한다. `dayjs(currentTime)` 은 브라우저 프레임으로 파싱해,
  // 브라우저 tz 가 서머타임 지역이면 전환일을 가로지르는 구간이 1시간 어긋났다.
  const base = toTimezoneKeepingWallClock(
    dayjs(currentTime, returnMatchingFormat(currentTime)),
    tz,
  );
  const fromTime = formatInTimezone(getSafeDateTime(base.subtract(offset, unit)), tz, timeFormat);
  const toTime = formatInTimezone(base, tz, timeFormat);

  return {
    timePeriod,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime }),
    isPaused,
  };
};

/**
 * 시간 범위로부터 TimePeriodInfo 생성
 */
export const getInitPeriodInfoFromTimeRange = (time: {
  fromTime: string;
  toTime: string;
  tz?: TimezoneName;
  timePeriod?: string;
  isPaused?: boolean;
}): TimePeriodInfo => {
  return {
    isPaused: time.isPaused ?? false,
    timePeriod: time.timePeriod ?? 'p10m',
    ...time,
    ...formatTimeRangeToUtc(time),
  };
};

/**
 * 일시정지 버튼 클릭 여부 확인
 */
export const isClickedPause = (
  prevTimeRange: TimePeriodInfo,
  newTimeRange: TimePeriodInfo,
): boolean =>
  !prevTimeRange.isPaused &&
  prevTimeRange.isPaused !== newTimeRange.isPaused &&
  prevTimeRange.fromTime === newTimeRange.fromTime &&
  prevTimeRange.toTime === newTimeRange.toTime;

// ============================================================
// Visibility Utilities - 가시성 판별 유틸리티
// ============================================================

/**
 * IntersectionObserver entry로부터 실제 가시성 상태를 판별.
 * - 뷰포트에 교차 중 → true
 * - 스크롤로 뷰포트를 벗어난 경우(dimensions > 0) → null (변경 없음)
 * - display:none 등으로 실제 숨겨진 경우(dimensions === 0) → false
 */
export const resolveIntersectionVisibility = (
  entry: Pick<IntersectionObserverEntry, 'isIntersecting' | 'boundingClientRect'>,
): boolean | null => {
  if (entry.isIntersecting) return true;
  const { width, height } = entry.boundingClientRect;
  if (width > 0 && height > 0) return null;
  return false;
};

// ============================================================
// Display Utilities - 표시 유틸리티
// ============================================================

/**
 * Live 모드 표시 시간 포맷
 */
export const getLiveDisplayTime = ({ toTime }: { toTime: string }): string =>
  dayjs(toTime, GLOBAL_TIME_RANGE_FORMAT).format(GLOBAL_TIME_LIVE_FORMAT);

/**
 * Range 모드 표시 시간 포맷
 */
export const getRangeDisplayTime = ({
  fromTime,
  toTime,
  dateOnly = false,
}: {
  fromTime: string;
  toTime: string;
  dateOnly?: boolean;
}): string => {
  if (!dateOnly) {
    return `${fromTime} ~ ${toTime}`;
  }

  const toDisplayDate = (value: string): string => {
    const format = returnMatchingFormat(value);
    if (format) {
      const parsed = dayjs(value, format);
      if (parsed.isValid()) {
        return parsed.format('YYYY.MM.DD');
      }
    }
    const [dateText] = value.split(' ');
    return dateText || value;
  };

  return `${toDisplayDate(fromTime)} ~ ${toDisplayDate(toTime)}`;
};

/**
 * 현재 시간 기준 초기 TimePeriodInfo 생성
 * TODO: 여기도 서버시간으로 돌리는게 맞으나, 변경 사항이 너무 많아 다음 기회에...
 */
export const getInitPeriodInfo = ({
  isPaused,
  timePeriod,
  timeFormat,
  timezone,
}: Partial<TimePeriodInfo> & {
  timeFormat?: string;
  timezone?: TimezoneName;
} = {}): TimePeriodInfo => {
  const DEFAULT_TIME_PERIOD = timePeriod ?? 'p10m';
  const { fromTime, toTime } = getCurrentTimeRange({
    period: DEFAULT_TIME_PERIOD,
    timeFormat,
    timezone,
  });

  return {
    timePeriod: DEFAULT_TIME_PERIOD,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime, timezone }),
    isPaused: isPaused ?? false,
  };
};
